"use client";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import YotoIcon from "~/components/icons/YotoIcon";
import YotoLovesCantecelul from "~/components/icons/YotoLovesCantecelul";
import Link from "~/components/Link";
import { useSnackbar } from "~/components/snackbar";
import { customerYotoService } from "~/connectors/yoto.connector";
import type { YotoCard, YotoIntegrationStatus } from "~/types";
import YotoCardItem from "./YotoCardItem";
import YotoInitialConfiguration from "./YotoInitialConfiguration";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

type SyncState = "idle" | "loading" | "error";

export default function YotoIntegrationContent() {
  const searchParams = useSearchParams();
  const { showSuccess, showError } = useSnackbar();
  const [integrationStatus, setIntegrationStatus] =
    useState<YotoIntegrationStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [syncState, setSyncState] = useState<SyncState>("idle");
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const [cards, setCards] = useState<YotoCard[]>([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [otherCardsExpanded, setOtherCardsExpanded] = useState(false);

  const oauthError = searchParams.get("error");
  const oauthErrorDescription = searchParams.get("error_description");

  const loadCards = useCallback(async () => {
    setLoadingCards(true);
    try {
      const { cards: fetchedCards } = await customerYotoService.getCards();
      setCards(fetchedCards);
    } catch {
      // ignore
    } finally {
      setLoadingCards(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const s = await customerYotoService.getStatus();
      setIntegrationStatus(s);
      if (s.connected) {
        loadCards();
      }
    } catch {
      // ignore
    } finally {
      setLoadingStatus(false);
    }
  }, [loadCards]);

  useEffect(() => {
    if (oauthError) {
      setCallbackError(
        oauthErrorDescription ?? "Autorizarea Yoto a eșuat. Încearcă din nou.",
      );
    }
  }, [oauthError, oauthErrorDescription, showSuccess]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleConnect = async () => {
    setConnectLoading(true);
    try {
      const { authorizationUrl } = await customerYotoService.getConnectUrl();
      window.location.href = authorizationUrl;
    } catch {
      setConnectLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncState("loading");
    try {
      const result = await customerYotoService.sync();
      setSyncState("idle");
      if (result.success) {
        showSuccess(result.message);
      } else {
        const errorDetails = result.errors?.length
          ? `: ${result.errors.join(", ")}`
          : "";
        showError(`${result.message}${errorDetails}`);
      }
      await loadStatus();
    } catch {
      setSyncState("error");
      showError("A apărut o eroare la sincronizare.");
    }
  };

  const handleDisconnectConfirm = async () => {
    setDisconnectDialogOpen(false);
    setDisconnectLoading(true);
    try {
      await customerYotoService.disconnect();
      setIntegrationStatus({ connected: false });
      setCards([]);
    } catch {
      // ignore
    } finally {
      setDisconnectLoading(false);
    }
  };

  const ourCards = cards.filter(
    (card) => card.cardId === integrationStatus?.playlistId,
  );
  const otherCards = cards.filter(
    (card) => card.cardId !== integrationStatus?.playlistId,
  );
  const pushedCount = integrationStatus?.pushedItemsCount ?? 0;
  const pushedLabel =
    pushedCount === 1
      ? "1 cântec sincronizat"
      : `${pushedCount} cântece sincronizate`;

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", px: 2, py: 6 }}>
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <YotoLovesCantecelul sx={{ mb: 2 }} />
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Conectează cântecele tale la playerul Yoto al copilului
        </Typography>
      </Box>
      {callbackError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setCallbackError(null)}
        >
          {callbackError}
        </Alert>
      )}
      {loadingStatus ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {/* Status Card */}
          {integrationStatus?.connected && (
            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center",
                    mb: 2
                  }}>
                  {integrationStatus?.connected ? (
                    <CheckCircleIcon color="yoto" sx={{ fontSize: 32 }} />
                  ) : (
                    <ErrorIcon color="disabled" sx={{ fontSize: 32 }} />
                  )}
                  <Box>
                    <Typography variant="h6" sx={{
                      fontWeight: 600
                    }}>
                      {integrationStatus?.connected
                        ? "Conectat la Yoto"
                        : "Neconectat"}
                    </Typography>
                    {integrationStatus?.connected &&
                      integrationStatus.lastSync && (
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>
                          Ultima sincronizare:{" "}
                          {new Date(integrationStatus.lastSync).toLocaleString(
                            "ro-RO",
                          )}
                        </Typography>
                      )}
                    {integrationStatus?.connected &&
                      !integrationStatus.lastSync && (
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>
                          Niciodată sincronizat
                        </Typography>
                      )}
                  </Box>
                </Stack>

                {integrationStatus?.connected && (
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    <Link href="/yoto/cantece" effect="color-on-hover">
                      {pushedLabel} - modifică
                    </Link>
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Errors */}
          {integrationStatus?.connected &&
            (integrationStatus.lastErrors?.length ?? 0) > 0 && (
              <Alert severity="warning">
                Ultima sincronizare a întâmpinat erori:{" "}
                {integrationStatus.lastErrors?.join(", ")}
              </Alert>
            )}

          {/* Actions */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              justifyContent: "center"
            }}
          >
            {integrationStatus?.connected ? (
              <Button
                variant="contained"
                color="yoto"
                startIcon={
                  syncState === "loading" ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <AutorenewIcon />
                  )
                }
                onClick={handleSync}
                disabled={syncState === "loading"}
              >
                Sincronizează cântecele
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                startIcon={
                  connectLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <YotoIcon />
                  )
                }
                onClick={handleConnect}
                disabled={connectLoading}
                color="yoto"
              >
                Conectează-te la contul tău Yoto
              </Button>
            )}
          </Stack>

          {/* Yoto Cards */}
          {integrationStatus?.connected && (
            <>
              <Divider />
              <Box>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center",
                    mb: 2
                  }}>
                  <Typography variant="h6" sx={{
                    fontWeight: 600
                  }}>
                    Cardurile tale Yoto
                  </Typography>
                  {loadingCards && <CircularProgress size={20} />}
                </Stack>

                {!loadingCards && cards.length === 0 && (
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    Nu există carduri Yoto disponibile.
                  </Typography>
                )}

                {/* Our platform cards - always visible */}
                <Stack spacing={1.5}>
                  {ourCards.map((card) => (
                    <YotoCardItem
                      key={card.cardId}
                      card={card}
                      isOurCard={true}
                      defaultExpanded={true}
                    />
                  ))}
                </Stack>

                {/* Other cards - hidden under collapsible */}
                {otherCards.length > 0 && (
                  <Box sx={{ mt: ourCards.length > 0 ? 1.5 : 0 }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setOtherCardsExpanded((prev) => !prev)}
                      endIcon={
                        otherCardsExpanded ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )
                      }
                      sx={{ mb: 1, color: "text.secondary" }}
                    >
                      {otherCardsExpanded
                        ? "Ascunde"
                        : `Vezi toate cardurile (${otherCards.length})`}
                    </Button>
                    <Collapse in={otherCardsExpanded}>
                      <Stack spacing={1.5}>
                        {otherCards.map((card) => (
                          <YotoCardItem
                            key={card.cardId}
                            card={card}
                            isOurCard={false}
                          />
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Setup instructions (shown after connection) */}
          {integrationStatus?.connected && (
            <>
              <Divider />
              <YotoInitialConfiguration />
            </>
          )}

          {/* How it works link */}
          <Divider />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Link href="/yoto/despre" variant="body2" effect="color-on-hover">
              Cum funcționează integrarea Yoto?
            </Link>
          </Box>

          {/* Disconnect button - at the bottom */}
          {integrationStatus?.connected && (
            <>
              <Divider />
              <Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDisconnectDialogOpen(true)}
                  disabled={disconnectLoading}
                >
                  {disconnectLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Deconectează Yoto"
                  )}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      )}
      {/* Disconnect confirmation dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
      >
        <DialogTitle>Deconectează Yoto</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ești sigur că vrei să deconectezi integrarea Yoto? Cântecele tale nu
            vor mai fi sincronizate automat pe player.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>
            Anulează
          </Button>
          <Button
            onClick={handleDisconnectConfirm}
            color="error"
            variant="contained"
          >
            Deconectează
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
